(() => {
  const storageKey = 'chromatica-theme';

  const applyTheme = (theme) => {
    document.documentElement.dataset.chromaticaTheme = theme;
    document.body?.setAttribute('data-chromatica-theme', theme);

    document.querySelectorAll('[data-chr-theme-toggle]').forEach((button) => {
      const dark = theme === 'dark';
      button.setAttribute('aria-pressed', String(dark));
      button.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
    });

    document.querySelectorAll('.archive-site').forEach((root) => {
      root.dataset.theme = theme;
    });
  };

  const initialTheme = () => {
    const saved = localStorage.getItem(storageKey);
    if (saved === 'dark' || saved === 'light') return saved;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const init = () => {
    applyTheme(initialTheme());

    document.querySelectorAll('[data-chr-theme-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const current = document.documentElement.dataset.chromaticaTheme;
        const next = current === 'dark' ? 'light' : 'dark';
        localStorage.setItem(storageKey, next);
        applyTheme(next);
      });
    });

    const menuButton = document.querySelector('[data-chr-menu-toggle]');
    const menuLabel = menuButton?.querySelector('[data-chr-menu-label]');
    const mobileMenu = document.querySelector('[data-chr-mobile-menu]');
    const desktopItems = [...document.querySelectorAll('[data-chr-desktop-menu-item]')];
    let desktopCloseTimer;

    const closeDesktopMenus = (except = null, returnFocus = false) => {
      window.clearTimeout(desktopCloseTimer);
      desktopItems.forEach((item) => {
        if (item === except) return;
        const trigger = item.querySelector('[data-chr-desktop-menu-trigger]');
        const wasOpen = item.classList.contains('is-open');
        item.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
        if (returnFocus && wasOpen) trigger?.focus();
      });
    };

    const openDesktopMenu = (item) => {
      if (!window.matchMedia('(min-width: 991px)').matches) return;
      closeDesktopMenus(item);
      item.classList.add('is-open');
      item.querySelector('[data-chr-desktop-menu-trigger]')?.setAttribute('aria-expanded', 'true');
    };

    desktopItems.forEach((item) => {
      const trigger = item.querySelector('[data-chr-desktop-menu-trigger]');

      item.addEventListener('mouseenter', () => {
        window.clearTimeout(desktopCloseTimer);
        openDesktopMenu(item);
      });

      item.addEventListener('mouseleave', () => {
        desktopCloseTimer = window.setTimeout(() => closeDesktopMenus(), 180);
      });

      trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = item.classList.contains('is-open');
        if (open) closeDesktopMenus();
        else openDesktopMenu(item);
      });
    });

    const setMobileMenu = (open) => {
      if (!menuButton || !mobileMenu) return;
      menuButton.setAttribute('aria-expanded', String(open));
      mobileMenu.hidden = !open;
      document.body.classList.toggle('chr-menu-open', open);
      if (menuLabel) menuLabel.textContent = open ? 'CLOSE' : 'MENU';
      if (!open) {
        mobileMenu.querySelectorAll('[data-chr-mobile-route][open]').forEach((details) => {
          details.removeAttribute('open');
        });
      }
    };

    menuButton?.addEventListener('click', () => {
      setMobileMenu(menuButton.getAttribute('aria-expanded') !== 'true');
    });

    mobileMenu?.querySelectorAll('[data-chr-mobile-route]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        mobileMenu.querySelectorAll('[data-chr-mobile-route][open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-chr-desktop-nav]')) closeDesktopMenus();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeDesktopMenus(null, true);
      if (menuButton?.getAttribute('aria-expanded') === 'true') {
        setMobileMenu(false);
        menuButton.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (window.matchMedia('(min-width: 991px)').matches) setMobileMenu(false);
      else closeDesktopMenus();
    }, { passive: true });

    /*
     * The Worldwide Proof Ledger counts once when it enters the viewport.
     * Values remain real Liquid output; motion only reveals them and never
     * changes the underlying number.
     */
    const statNumbers = document.querySelectorAll('[data-chr-count]');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const numberFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en');

    const revealStat = (node) => {
      if (node.dataset.chrCountRevealed === 'true') return;
      node.dataset.chrCountRevealed = 'true';

      const target = Number(node.dataset.chrCount);
      if (!Number.isFinite(target) || reduceMotion) {
        node.textContent = Number.isFinite(target) ? numberFormatter.format(target) : node.textContent;
        return;
      }

      const duration = Number(node.dataset.chrCountDuration) || 1050;
      const startedAt = performance.now();
      const tick = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        node.textContent = numberFormatter.format(Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if ('IntersectionObserver' in window && !reduceMotion) {
      const statObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealStat(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: .35 });
      statNumbers.forEach((node) => statObserver.observe(node));
    } else {
      statNumbers.forEach(revealStat);
    }

    const updateCartCounts = (count) => {
      if (!Number.isFinite(count)) return;
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = String(count);
      });
    };

    const fetchCurrentCartCount = () => {
      const cartUrl = window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart.js`
        : '/cart.js';

      return fetch(cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          return response.json();
        })
        .then((cart) => updateCartCounts(Number(cart.item_count)))
        .catch((error) => console.warn('[chromatica-cart-count]', error));
    };

    /*
     * Horizon emits Shopify's standard cart event. Its promise resolves only
     * after /cart/add has succeeded, so the custom header always shows the
     * authoritative total without waiting for a page navigation.
     */
    document.addEventListener('shopify:cart:lines-update', (event) => {
      const eventPromise = event.promise || event.detail?.promise;

      if (eventPromise && typeof eventPromise.then === 'function') {
        eventPromise
          .then(({ cart }) => {
            const count = Number(cart?.totalQuantity);
            if (Number.isFinite(count)) updateCartCounts(count);
            else fetchCurrentCartCount();
          })
          .catch(() => fetchCurrentCartCount());
        return;
      }

      fetchCurrentCartCount();
    });

    /* Compatibility with app/legacy AJAX cart events. */
    document.addEventListener('cart:update', (event) => {
      const count = Number(event.detail?.data?.item_count ?? event.detail?.cart?.item_count);
      if (Number.isFinite(count)) updateCartCounts(count);
      else fetchCurrentCartCount();
    });

    let chrFilterSequence = 0;
    const initFilterDNA = (scope = document) => {
      const roots = new Set();
      if (scope?.matches?.('.chr-filter-dna')) roots.add(scope);
      scope?.querySelectorAll?.('.chr-filter-dna').forEach((node) => roots.add(node));

      roots.forEach((filter) => {
        if (filter.dataset.chrMobileFilterReady === 'true') return;

        const controls = filter.querySelector('.chr-filter-dna__controls');
        const actions = filter.querySelector('.chr-filter-dna__head-actions');
        if (!controls || !actions) return;

        filter.dataset.chrMobileFilterReady = 'true';
        chrFilterSequence += 1;

        if (!controls.id) controls.id = `ChrFilterControls-${chrFilterSequence}`;

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'chr-filter-dna__mobile-toggle';
        toggle.setAttribute('aria-controls', controls.id);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open filters');
        toggle.textContent = 'FILTERS +';
        actions.prepend(toggle);

        const setOpen = (open, returnFocus = false) => {
          filter.classList.toggle('is-mobile-open', open);
          toggle.setAttribute('aria-expanded', String(open));
          toggle.setAttribute('aria-label', open ? 'Close filters' : 'Open filters');
          toggle.textContent = open ? 'FILTERS ×' : 'FILTERS +';
          if (returnFocus) toggle.focus();
        };

        toggle.addEventListener('click', () => {
          setOpen(toggle.getAttribute('aria-expanded') !== 'true');
        });

        filter.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !filter.classList.contains('is-mobile-open')) return;
          setOpen(false, true);
        });

        const media = window.matchMedia('(max-width: 760px)');
        const syncViewport = () => {
          if (media.matches) setOpen(false);
          else {
            filter.classList.remove('is-mobile-open');
            toggle.setAttribute('aria-expanded', 'true');
            toggle.setAttribute('aria-label', 'Filters');
            toggle.textContent = 'FILTERS +';
          }
        };

        if (typeof media.addEventListener === 'function') media.addEventListener('change', syncViewport);
        else if (typeof media.addListener === 'function') media.addListener(syncViewport);
        syncViewport();
      });
    };

    const updateBackTop = () => {
      document.querySelectorAll('[data-chr-back-top]').forEach((button) => {
        button.classList.toggle('is-visible', window.scrollY > 700);
      });
    };
    const initBackTop = (scope = document) => {
      scope.querySelectorAll('[data-chr-back-top]').forEach((button) => {
        if (button.dataset.chrBackTopReady === 'true') return;
        button.dataset.chrBackTopReady = 'true';
        button.addEventListener('click', () => {
          window.scrollTo({
            top: 0,
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        });
      });
      updateBackTop();
    };
    window.addEventListener('scroll', updateBackTop, { passive: true });
    document.addEventListener('shopify:section:load', (event) => {
      initFilterDNA(event.target);
      initBackTop(event.target);
    });
    initFilterDNA();
    initBackTop();

    const judgeTriggerSelector = [
      '.jdgm-write-rev-link',
      '.jdgm-write-review',
      '[data-testid="write-review-button"]',
      '[data-testid*="write-review"]',
      '[data-action="write-review"]',
      '[aria-label*="Write a review" i]'
    ].join(',');

    const judgeFormSelector = [
      '.jdgm-form-wrapper',
      '.jdgm-review-form',
      '[data-testid="review-form"]',
      '[data-testid*="review-form"]',
      '.jdgm-dialog form',
      '.jdgm-modal form'
    ].join(',');

    const findJudgeTrigger = (reviewSection) => {
      const app = reviewSection?.querySelector('[data-chr-review-app]');
      const directMatch = app?.querySelector(judgeTriggerSelector);
      if (directMatch) return directMatch;

      return Array.from(app?.querySelectorAll('button, a, [role="button"]') || []).find((element) =>
        /write\s+(a\s+)?review|leave\s+(a\s+)?review/i.test(element.textContent?.trim() || '')
      );
    };

    const findJudgeForm = (reviewSection) =>
      reviewSection.querySelector(judgeFormSelector) || document.querySelector(judgeFormSelector);

    const openJudgeReviewForm = (reviewSection, shouldScroll = true) => {
      const existingForm = findJudgeForm(reviewSection);
      if (existingForm && existingForm.getClientRects().length) {
        if (shouldScroll) existingForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }

      const nativeTrigger = findJudgeTrigger(reviewSection);
      if (!nativeTrigger) return false;

      /*
       * Use the element's native click method. Judge.me attaches its opening
       * logic directly to this control in some widget versions; dispatching a
       * synthetic MouseEvent can miss that listener and leave a dead button.
       */
      nativeTrigger.click();

      window.setTimeout(() => {
        const form = findJudgeForm(reviewSection);
        const scrollTarget = form || reviewSection.querySelector('[data-chr-review-app]');
        if (shouldScroll) scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 180);

      return true;
    };

    /*
     * Use delegated handling because Judge.me initializes after the theme.
     * A click is queued until the native widget is ready instead of becoming
     * a dead control when the app is still loading.
     */
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-chr-judge-write]');
      if (!trigger) return;

      event.preventDefault();
      const reviewSection = trigger.closest('[data-chr-reviews]');
      if (!reviewSection) return;

      if (openJudgeReviewForm(reviewSection)) return;

      reviewSection.dataset.chrPendingReviewOpen = 'true';
      reviewSection.querySelector('[data-chr-review-app]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    /*
     * The compact rating beside the product price is navigation, not a second
     * review widget. Existing ratings open and reveal the review archive.
     * The zero-review CTA additionally asks Judge.me to open its native form.
     */
    document.addEventListener('click', (event) => {
      const productReviewLink = event.target.closest(
        '[data-chr-review-summary], [data-chr-review-write-link]'
      );
      if (!productReviewLink) return;

      const reviewSection = document.querySelector('#chromatica-reviews');
      if (!reviewSection) return;

      event.preventDefault();
      const panel = reviewSection.querySelector('.chr-reviews__panel');
      if (panel instanceof HTMLDetailsElement) panel.open = true;

      reviewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (!productReviewLink.matches('[data-chr-review-write-link]')) return;

      window.setTimeout(() => {
        if (openJudgeReviewForm(reviewSection, false)) return;
        reviewSection.dataset.chrPendingReviewOpen = 'true';
      }, 350);
    });

    /*
     * Judge.me renders asynchronously and may rebuild its widget. Observe the
     * real DOM without a short timeout. The count probe comes from Judge.me's
     * product badge metafield, so zero-review products are detected even when
     * the Review Widget initially has no visible content.
     */
    document.querySelectorAll('[data-chr-reviews]').forEach((reviewSection) => {
      const app = reviewSection.querySelector('[data-chr-review-app]');
      if (!app) return;

      const syncReviewState = () => {
        const countSource = reviewSection.querySelector(
          '.chr-review-count-probe [data-number-of-reviews], [data-chr-review-app] [data-number-of-reviews]'
        );
        const rawCount = countSource?.getAttribute('data-number-of-reviews');
        const reviewCount = rawCount == null || rawCount === '' ? null : Number(rawCount);

        if (Number.isFinite(reviewCount)) {
          reviewSection.classList.toggle('chr-reviews--empty', reviewCount === 0);
        }

        if (reviewSection.dataset.chrPendingReviewOpen === 'true' && openJudgeReviewForm(reviewSection)) {
          delete reviewSection.dataset.chrPendingReviewOpen;
        }
      };

      const observer = new MutationObserver(syncReviewState);
      observer.observe(app, { childList: true, subtree: true, attributes: true });
      const countProbe = reviewSection.querySelector('.chr-review-count-probe');
      if (countProbe) {
        observer.observe(countProbe, { childList: true, subtree: true, attributes: true });
      }
      syncReviewState();

      /*
       * Some zero-review products have an entirely blank badge metafield. Once
       * Judge.me has had time to render, a native write control with no review
       * cards is the reliable zero-review fallback. Keep the native empty
       * state visible; only an explicit customer click opens the form.
       */
      window.setTimeout(() => {
        const hasCount = reviewSection.querySelector(
          '.chr-review-count-probe [data-number-of-reviews], [data-chr-review-app] [data-number-of-reviews]'
        );
        const hasReview = app.querySelector(
          '.jdgm-rev, [data-testid="review-card"], [data-testid*="review-item"]'
        );

        if (!hasCount && !hasReview && findJudgeTrigger(reviewSection)) {
          reviewSection.classList.add('chr-reviews--empty');
        }
      }, 3000);
    });
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init, { once: true })
    : init();
})();
